import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CoreModule } from '../../../core/core.module';
import { TranslationService } from '../../../core/services/translate-service';
import { AuthService } from '../../../features/auth/auth.service';
import { DynamicPopupComponent } from '../dynamic-popup/dynamic-popup.component';

@Component({
  selector: 'app-side-menu',
  standalone: true,
  imports: [CommonModule, RouterModule, DynamicPopupComponent, CoreModule],
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent {
  canViewUsers: boolean = false;
  canViewRoles: boolean = false;
  canViewNotifications: boolean = false;
  canViewEntities: boolean = false;
  canViewSlaConfig: boolean = false;
  canViewReports: boolean = false;
  canViewSystemLogs: boolean = false;
  showLanguagesMenu = false;
  isCollapsed: boolean = false;
  activeSubmenu: string | null = null;
  currentRoute: string = '';
  isPopupVisible = false;
  showViewPopup = false;
  selectedObject: any;
  selectedObjectType: string = '';
  isRegulator: boolean = false; // flag derived from entityType

  constructor(
    private router: Router,
    private authService: AuthService,
    private translationService: TranslationService,
  ) {
    // Determine if current user is a regulator
    this.isRegulator = this.authService.getEntityType() === 'Regulator';
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects;
        this.checkActiveSubmenu();
        // Refresh flag in case entityType was set after initial load
        this.isRegulator = this.authService.getEntityType() === 'Regulator';
      }
      this.canViewUsers = this.authService.hasPermission('read:users');
      this.canViewEntities = this.authService.hasPermission('read:entities');
      this.canViewNotifications =
        this.authService.hasPermission('read:notifications');
      this.canViewRoles = this.authService.hasPermission('read:roles');
      this.canViewReports = this.authService.hasPermission('read:roles');
      this.canViewSystemLogs =
        this.authService.hasPermission('read:systemLogs');
    });
    this.updateCollapseState(window.innerWidth);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects;
        this.checkAndExpandSubmenu(this.currentRoute);
      }
    });
  }
  checkAndExpandSubmenu(currentUrl: string): void {
    if (
      currentUrl.includes('/user-management') ||
      currentUrl.includes('/user') ||
      currentUrl.includes('/roles')
    ) {
      this.activeSubmenu = 'dashboard/user-management';
    } else if (currentUrl.includes('/entity-management')) {
      this.activeSubmenu = 'dashboard/entity-management';
    } else if (currentUrl.includes('/reports/')) {
      this.activeSubmenu = 'dashboard/reports';
    } else if (
      currentUrl.includes('/regulator-home') ||
      currentUrl.includes('/dashboard/home')
    ) {
      this.activeSubmenu = 'dashboard/home';
    } else {
      this.activeSubmenu = null;
    }
  }

  switchLanguage(lang: string): void {
    this.translationService.setLanguage(lang);
    this.showLanguagesMenu = false;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.updateCollapseState(event.target.innerWidth);
  }

  updateCollapseState(width: number): void {
    if (width < 992) {
      this.isCollapsed = true;
    } else {
      this.isCollapsed = false;
    }
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;

    if (this.isCollapsed) {
      this.activeSubmenu = null;
    }
  }
  toggleSubmenu(submenu: string): void {
    if (this.isCollapsed) {
      return; // Don't allow toggling if sidebar is collapsed
    }
    if (this.activeSubmenu === submenu) {
      this.activeSubmenu = null;
    } else {
      this.activeSubmenu = submenu;
    }
  }

  checkActiveSubmenu(): void {
    if (this.currentRoute.includes('/user-management')) {
      this.activeSubmenu = 'dashboard/user-management';
    } else if (this.currentRoute.includes('/entity-management')) {
      this.activeSubmenu = 'dashboard/entity-management';
    } else if (this.currentRoute.includes('/reports/')) {
      this.activeSubmenu = 'dashboard/reports';
    } else if (
      this.currentRoute.includes('/regulator-home') ||
      this.currentRoute.endsWith('/dashboard/home')
    ) {
      this.activeSubmenu = 'dashboard/home';
    } else {
      this.activeSubmenu = null;
    }
  }

  isSubmenuActive(submenu: string): boolean {
    return this.activeSubmenu === submenu;
  }

  isParentActive(parent: string): boolean {
    if (parent === 'user-management') {
      return (
        this.currentRoute.includes('/user-management') ||
        this.currentRoute.includes('/user') ||
        this.currentRoute.includes('/view-user') ||
        this.currentRoute.includes('/role')
      );
    } else if (parent === 'entity-management') {
      return this.currentRoute.includes('/entity-management');
    } else if (parent === 'reports') {
      return this.currentRoute.includes('/reports');
    }
    return false;
  }

  openPopup(type: string): void {
    // Regulators shouldn't open notifications or language? Only disabling target menus; keep languages allowed.
    if (this.isRegulator && type === 'notifications') {
      return; // block notifications popup
    }
    if (type === 'notifications') {
    } else if (type === 'languages') {
      this.showLanguagesMenu = !this.showLanguagesMenu;
    } else if (type === 'logout') {
    } else if (type === 'user') {
    }
  }

  logoutPopup(): void {
    this.isPopupVisible = true;
  }

  logout(): void {
    this.authService.logout();
    this.isPopupVisible = false;
  }
  viewDetails(User: any) {
    this.selectedObject = User;
    this.selectedObjectType = 'User';
    this.showViewPopup = true;
  }

  closePopup(): void {
    this.showViewPopup = false;
  }

  closeLogoutPopup(): void {
    this.isPopupVisible = false;
  }
  isNotificationRoute(): boolean {
    return (
      this.router.url.startsWith('/dashboard/notifications') ||
      this.router.url.startsWith('/dashboard/notification-item')
    );
  }
  isEntityRoute(): boolean {
    return (
      this.router.url.startsWith('/dashboard/entity/edit-entity') ||
      this.router.url.startsWith(
        '/dashboard/entity-management/create-entity',
      ) ||
      this.router.url.startsWith('/dashboard/entities') ||
      this.router.url.startsWith('/dashboard/entity-management/entities')
    );
  }
  isUsersRoute(): boolean {
    return (
      this.router.url.startsWith('/dashboard/users') ||
      this.router.url.startsWith('/dashboard/user-management/users') ||
      this.router.url.startsWith('/dashboard/view-user/') ||
      this.router.url.startsWith('/dashboard/user/edit-user/') ||
      this.router.url.startsWith('/dashboard/user-management/create-user')
    );
  }
  isRolesRoute(): boolean {
    return (
      this.router.url.startsWith('/dashboard/user-management/create-role') ||
      this.router.url.startsWith('/dashboard/user-management/roles') ||
      this.router.url.startsWith('/dashboard/role/edit-role') ||
      this.router.url.startsWith('/dashboard/roles')
    );
  }
  isLogsRoute(): boolean {
    return this.router.url.startsWith('/dashboard/logs-management');
  }
  isSlaRoute(): boolean {
    return (
      this.router.url.startsWith('/dashboard/sla/create-sla') ||
      this.router.url.startsWith('/dashboard/sla/view-sla') ||
      this.router.url.startsWith('/dashboard/sla/edit-sla') ||
      this.router.url.startsWith('/dashboard/sla-configuration')
    );
  }
  isReportsRoute(): boolean {
    return this.router.url.startsWith('/dashboard/reports');
  }

  isReportsChild(child: string): boolean {
    return this.router.url.startsWith(`/dashboard/reports/${child}`);
  }

  isHomeActive(): boolean {
    return (
      this.router.url.startsWith('/dashboard/regulator-home') ||
      this.router.url === '/dashboard/home'
    );
  }

  isRegulatorHomeRoute(): boolean {
    return this.router.url.startsWith('/dashboard/regulator-home');
  }

  isDashboardHomeRoute(): boolean {
    return this.router.url === '/dashboard/home';
  }
}
